import { useState } from 'react';
import { Button, Drawer, Group, Radio, Indicator, Stack } from '@mantine/core';
import { FiFilter } from 'react-icons/fi';
import { AliasStatus, AliasType } from '../types';

const statusOptions = [
    { value: 'ENABLED', label: 'Enabled' },
    { value: 'DISABLED', label: 'Disabled' },
];

const typeOptions = [
    { value: 'USER', label: 'User' },
    { value: 'SYSTEM', label: 'System' },
];

export interface AliasFilters {
    aliasStatus: AliasStatus | null;
    aliasType: AliasType | null;
}

interface FilterRecordsButtonProps {
    onFilterChange: (filters: AliasFilters) => void;
    defaultFilters?: AliasFilters;
}

const defaultFilterValues: AliasFilters = { aliasStatus: null, aliasType: null };

export const FilterRecordsButton = ({ onFilterChange, defaultFilters = defaultFilterValues }: FilterRecordsButtonProps) => {
    const [opened, setOpened] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<AliasStatus | null>(defaultFilters.aliasStatus);
    const [selectedType, setSelectedType] = useState<AliasType | null>(defaultFilters.aliasType);
    const [appliedFilters, setAppliedFilters] = useState<AliasFilters>(defaultFilters);

    const hasActiveFilter = appliedFilters.aliasStatus !== null || appliedFilters.aliasType !== null;

    const handleApply = () => {
        const filters = { aliasStatus: selectedStatus, aliasType: selectedType };
        onFilterChange(filters);
        setAppliedFilters(filters);
        setOpened(false);
    };

    const handleClear = () => {
        setSelectedStatus(null);
        setSelectedType(null);
        const filters = { aliasStatus: null, aliasType: null };
        onFilterChange(filters);
        setAppliedFilters(filters);
        setOpened(false);
    };

    return (
        <>
            <Indicator disabled={!hasActiveFilter} size={12} color="red">
                <Button
                    variant="outline"
                    leftSection={<FiFilter size={16} />}
                    onClick={() => setOpened(true)}
                >
                    Filter
                </Button>
            </Indicator>

            <Drawer
                opened={opened}
                onClose={() => setOpened(false)}
                title="Filter Aliases"
                position="right"
                size="sm"
            >
                <Stack gap="md">
                    <Radio.Group
                        label="Status"
                        value={selectedStatus || ''}
                        onChange={(value) => setSelectedStatus(value as AliasStatus || null)}
                    >
                        <Group mt="xs" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <Radio value="" label="All" />
                            {statusOptions.map((option) => (
                                <Radio key={option.value} value={option.value} label={option.label} />
                            ))}
                        </Group>
                    </Radio.Group>

                    <Radio.Group
                        label="Type"
                        value={selectedType || ''}
                        onChange={(value) => setSelectedType(value as AliasType || null)}
                    >
                        <Group mt="xs" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <Radio value="" label="All" />
                            {typeOptions.map((option) => (
                                <Radio key={option.value} value={option.value} label={option.label} />
                            ))}
                        </Group>
                    </Radio.Group>
                </Stack>

                <Group justify="flex-end" mt="md" gap="xs">
                    <Button variant="outline" color="red" onClick={handleClear}>
                        Clear
                    </Button>
                    <Button onClick={handleApply}>
                        Apply
                    </Button>
                </Group>
            </Drawer>
        </>
    );
};
